import moment from "moment";
import React, { useEffect, useState } from "react";
import { Spinner, Table } from "reactstrap";
import relatoriosService, {
  RelatorioNotasDaTurmaNotaResponse,
  RelatorioNotasDaTurmaResponse,
} from "../../../api/relatorios";
import { TurmaResponse } from "../../../api/turmas";
import { errorToString } from "../../../helpers/funcoes";
import { CelulaNota } from "./CelulaNota";
import { ModalCorrecoes } from "./ModalCorrecoes";
import { ModalExclusao } from "./ModalExclusao";
import { ResumoGrupos } from "./ResumoGrupos";
import "./TabelaNotas.css";

export function TabelaNotas(props: {
  onError: (error?: string) => void;
  turma?: TurmaResponse;
}) {
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState<RelatorioNotasDaTurmaResponse>();
  const [notaToView, setNotaToView] =
    useState<RelatorioNotasDaTurmaNotaResponse>();
  const [notaToDelete, setNotaToDelete] =
    useState<RelatorioNotasDaTurmaNotaResponse>();

  const loadRelatorio = () => {
    if (props.turma) {
      setLoading(true);
      relatoriosService
        .notasDaTurma(props.turma)
        .then((payload) => setPayload(payload))
        .catch((err) => props.onError(errorToString(err)))
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    loadRelatorio();
    return () => setPayload(undefined);
  }, [props.turma]);

  if (loading) {
    return <Spinner />;
  }
  if (!payload) {
    return <></>;
  }

  const alunos = payload.alunos;
  const mapa: {
    [k: string]: RelatorioNotasDaTurmaNotaResponse | undefined;
  } = Object.fromEntries(
    payload.notas.map((i) => [`${i.alunoId}_${i.atividadeId}`, i]),
  );
  const datas: Record<string, moment.Moment> = {};

  for (const n of payload.notas) {
    const d = datas[n.atividadeId];
    const nd = moment(n.createdAt);
    if (!d || nd.isBefore(d)) {
      datas[n.atividadeId] = nd;
    }
  }

  const atividades = payload.atividades.map((i) => ({
    id: i.id,
    nome: i.nome,
    dateTime: datas[i.id],
  }));

  function mediaDaAtividade(atividadeId: string) {
    let count = 0;
    let soma = 0;
    for (const a of alunos) {
      const n = mapa[`${a.id}_${atividadeId}`]?.nota;
      if (n !== undefined) {
        count++;
        soma += n;
      }
    }
    if (count == 0) return undefined;
    return soma / count;
  }

  function mediaDoAluno(alunoId: string) {
    let count = 0;
    let soma = 0;
    for (const a of atividades) {
      const n = mapa[`${alunoId}_${a.id}`]?.nota;
      if (n !== undefined) {
        count++;
        soma += n;
      }
    }
    if (count == 0) return undefined;
    return soma / count;
  }

  return (
    <>
      <Table className="tbNotas">
        <thead>
          <tr>
            <th colSpan={2}>&nbsp;</th>
            {payload.atividades.map((a, i) => (
              <th className="text-center" key={a.id}>
                Ativ. {i + 1}
              </th>
            ))}
          </tr>
          <tr>
            <th colSpan={2} className="text-end">
              Atividade
            </th>
            {atividades.map((a) => (
              <td className="text-center" key={a.id} title={a.nome}>
                {abreviar(a.nome)}
              </td>
            ))}
          </tr>
          <tr>
            <th colSpan={2} className="text-end">
              Data
            </th>
            {atividades.map((a) => (
              <td className="text-center" key={a.id}>
                {a.dateTime.format("DD/MM")}
              </td>
            ))}
          </tr>
          <tr>
            <th colSpan={2} className="text-end">
              Média
            </th>
            {atividades.map((a) => (
              <CelulaNota
                className="fw-bold fs-5"
                key={a.id}
                nota={mediaDaAtividade(a.id)}
              />
            ))}
          </tr>
          <tr>
            <th>Aluno</th>
            <th className="text-center">Média</th>
            {atividades.map((a) => (
              <td className="text-center" key={a.id}>
                &nbsp;
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {alunos.map((a) => (
            <tr key={a.id}>
              <th scope="row">
                <span className="relatorioNomeDoAluno" title={a.nome}>
                  {a.nome}
                </span>
              </th>
              <CelulaNota className="fw-bold fs-5" nota={mediaDoAluno(a.id)} />
              {atividades.map((t) => {
                const model = mapa[`${a.id}_${t.id}`];
                if (model) {
                  return (
                    <CelulaNota
                      key={t.id}
                      nota={model.nota}
                      paginas={model.paginas}
                      onView={() => {
                        props.onError(undefined);
                        setNotaToView(model);
                      }}
                      onDelete={() => {
                        props.onError(undefined);
                        setNotaToDelete(model);
                      }}
                    >
                      <ResumoGrupos model={model} />
                    </CelulaNota>
                  );
                } else {
                  return <CelulaNota key={t.id} />;
                }
              })}
            </tr>
          ))}
        </tbody>
      </Table>
      <ModalCorrecoes
        nota={notaToView}
        onClose={() => {
          setNotaToView(undefined);
        }}
        onError={(e) => {
          setNotaToView(undefined);
          props.onError(e);
        }}
      />
      <ModalExclusao
        relatorio={payload}
        nota={notaToDelete}
        onClose={() => {
          setNotaToDelete(undefined);
        }}
        onError={(e) => {
          setNotaToDelete(undefined);
          props.onError(e);
        }}
        onRefresh={() => loadRelatorio()}
      />
    </>
  );
}

function abreviar(s: string): string {
  return s.length < 10 ? s : s.substring(0, 10) + "…";
}
